import { Box, FormLabel, TextField, TextFieldProps, Typography, InputAdornment } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';
import cx from 'classnames';
import React, { forwardRef, Ref, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import InputPopup, { IFormTextFieldInputPopupImperativeHandle } from './InputPopup';

interface IOwnProps {
  onValueChange?: (v: string) => void;
  onValueSubmit?: (v: string) => void;
  defaultValue?: string;
  isEditing?: boolean;
  isLoading?: boolean;
  usePopup?: boolean;
  validators?: IFormTextFieldValidator[];
  maxLength?: number;
  showTextCount?: boolean;
}

type IProps = IOwnProps & TextFieldProps;

export interface IFormTextFieldImperativeHandle {
  clear: () => void;
  getValidationMessages: () => string[];
  getValue: () => string;
  clearValidationMessage: () => void;
}

export interface IFormTextFieldValidator {
  hint: string;
  rule: (value: string) => boolean;
}

export const CommonValidator: { [name: string]: IFormTextFieldValidator } = {
  Required: {
    hint: 'Required field.',
    rule: (value = '') => Boolean(value),
  },

  Email: {
    hint: 'Invalid email format.',
    rule: (value = '') =>
      value === '' ||
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        value,
      ),
  },
};

const useStyles = makeStyles((theme: Theme) => ({
  wrap: {
    position: 'relative',
  },

  formLabel: { position: 'absolute', left: 0, top: 1, fontSize: 12 },

  immutableTextField: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '1.1876em',
    padding: '4px 0 5px',
  },

  textCount: {
    fontSize: 12,
    color: '#000',
    opacity: 0.54,
  },
  curCount: {
    fontWeight: 'bold',
    marginRight: 3,
  },
}));

const FormTextField = (props: IProps, ref: Ref<unknown>): JSX.Element => {
  const classes = useStyles();

  const {
    name,
    defaultValue = '',
    onValueSubmit,
    onValueChange,
    isLoading = false,
    isEditing = true,
    usePopup = false,
    required = false,
    validators = [],
    placeholder,
    label,
    InputProps,
    InputLabelProps,
    inputProps,
    className,
    maxLength,
    showTextCount,
    ...restProps
  } = props;

  const [text, setTextState] = useState<string>(defaultValue);
  const textRef = useRef<string>(defaultValue);

  const setText = (value: string) => {
    setTextState(value);
    textRef.current = value;
  };

  const [validationMessage, setValidationMessage] = useState<string>();
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const popupRef = useRef<IFormTextFieldInputPopupImperativeHandle>();

  useEffect(() => {
    setText(defaultValue || '');
  }, [defaultValue]);

  const getValidationMessages = useCallback(
    (value: string): string[] => {
      return Array<IFormTextFieldValidator>()
        .concat(required ? [CommonValidator.Required] : [], validators)
        .filter(({ rule }) => !rule(value))
        .map(({ hint }) => hint);
    },
    [required, validators],
  );

  useImperativeHandle<unknown, IFormTextFieldImperativeHandle>(
    ref,
    () => ({
      clear: () => setText(''),
      getValidationMessages: () => getValidationMessages(textRef.current).map(message => `${name}: ${message}`),
      getValue: () => text,
      clearValidationMessage: () => {
        setValidationMessage('');
      },
    }),
    [getValidationMessages, text, name],
  );

  const submit = (value: string) => {
    const validationMessages = getValidationMessages(value);
    const isEmpty = !required && !value;

    setValidationMessage(isEmpty ? '' : validationMessages[0]);
    onValueSubmit?.(value);
  };

  const handleFocusForPopup = () => {
    if (usePopup) {
      setShowPopup(true);
      popupRef.current?.focus();
    }
  };

  const handlePopupCancel = () => {
    setShowPopup(false);
  };

  const handlePopupEnterValue = (value: string) => {
    setText(value);
    submit(value);
    setShowPopup(false);
  };

  return (
    <Box className={cx(classes.wrap, className)}>
      {isEditing && (
        <TextField
          name={name}
          value={text}
          label={label}
          onChange={e => {
            const text = maxLength ? e.target.value.substring(0, maxLength) : e.target.value;
            setText(text);
            onValueChange?.(text);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              submit(text);
            }
          }}
          onFocus={handleFocusForPopup}
          onBlur={() => {
            if (!usePopup) {
              submit(text);
            }
          }}
          error={!!validationMessage}
          helperText={validationMessage}
          InputLabelProps={{ ...InputLabelProps, shrink: true }}
          placeholder={placeholder}
          inputProps={{ style: { fontSize: '14px' }, ...inputProps }}
          InputProps={{
            ...InputProps,
            endAdornment:
              showTextCount && maxLength ? (
                <InputAdornment position="end" className={classes.textCount}>
                  <span className={classes.curCount}>{text.length}</span> / {maxLength}
                </InputAdornment>
              ) : (
                InputProps?.endAdornment || null
              ),
          }}
          {...restProps}
        />
      )}
      {(!isEditing || isLoading) && (
        <>
          <FormLabel component="div" className={classes.formLabel} style={InputLabelProps?.style}>
            {label}
          </FormLabel>
          <Box className={classes.immutableTextField} marginTop={label ? '16px' : 0} style={inputProps?.style}>
            {isLoading && <Skeleton style={{ width: '100%' }} />}

            {!isLoading && (
              <>
                {InputProps?.startAdornment}
                <Typography
                  style={{
                    margin: 0,
                    padding: 0,
                    fontSize: '14px',
                    ...inputProps?.style,
                    opacity: text ? 1 : 0.38,
                  }}
                >
                  {text || placeholder || ''}
                </Typography>
              </>
            )}
          </Box>
        </>
      )}
      {showPopup && (
        <InputPopup defaultValue={text} placeholder={placeholder} onCancel={handlePopupCancel} onValueSubmit={handlePopupEnterValue} ref={popupRef} />
      )}
    </Box>
  );
};

export default forwardRef(FormTextField);
